import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBox,
  CmButtonDatePicker,
  CmFlex,
  CmFormControl,
  CmFormLabel,
  CmLongLoading,
  CmRadio,
  CmRadioGroup,
  CmScrollBox,
  CmSelectDigitsTime,
  CmStack,
  SelectForm,
} from '@/components/uiParts';
import { DateDomainService } from '@/domain/services';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import {
  BUTTON_AREA_ID,
  MenuManagementNewButtonArea,
} from '@/pages/menu-managements/components/TopButtonArea';
import { MenuManagementCardWrapper } from '@/pages/menu-managements/components/Wrapper';
import {
  calcMinTime,
  deliveryMethodDateAndTime,
  deliveryMethodImmediately,
  DeliveryMethodKey,
  deliveryMethodMap,
  isValidateBeforeDate,
  MIN_SCHEDULED_DATE_PLUS_MINUTES,
} from '@/pages/menu-managements/executions/service';
import { routes } from '@/router';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useMenuManagementCreateExecution } from '@/services/Execution/hooks';

const MenuManagementExecutionsNewPage: React.FC = (): JSX.Element => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const { datetimeNoColonFormat } = DateDomainService;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deliveryMethod, setDeliveryMethod] =
    useState<DeliveryMethodKey>('immediately');
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [brandSelect, setBrandSelect] = useState<SelectForm | null>({
    label: '',
    value: '',
  });
  // このプラスの10分に特に根拠はなし。
  const [scheduledDate, setScheduledDate] = useState<Date>(calcMinTime(10));
  const handleInputScheduledDate = (date: Date) => {
    setScheduledDate(date);
  };

  const handleChangeDeliveryMethod = (deliveryMethod: DeliveryMethodKey) => {
    setDeliveryMethod(deliveryMethod);
  };

  const associateBrandSelectInput = {
    value: brandSelect,
    options: associateBrandSelectOptions,
    onChange: (value: SelectForm | null) => {
      setBrandSelect(value);
    },
    onReset: () => {
      setBrandSelect({ value: '', label: '' });
    },
  };

  const handleChangeTime = (
    time: string,
    targetMethod: 'setHours' | 'setMinutes'
  ) => {
    const copyNewTime = scheduledDate;
    const newTargetTime = new Date(copyNewTime.getTime());
    newTargetTime[targetMethod](Number(time));
    setScheduledDate(newTargetTime);
  };

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, BUTTON_AREA_ID]);

  const listUrl = routes.menuManagementExecutions.path;

  const initializeNewPageState = async () => {
    const associateBrands = await useAssociateBrandList();
    const associateBrandOptions = associateBrands.map((brand) => {
      return {
        label: brand.name,
        value: brand.id.toString(),
      };
    });
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    const initialAssociateBrand =
      associateBrand !== undefined ? associateBrand : associateBrandOptions[0];
    LocalStorageHandler.set('menuManagement', {
      associateBrand: initialAssociateBrand,
    });
    setBrandSelect(initialAssociateBrand);
    setAssociateBrandSelectOptions(associateBrandOptions);
  };

  useEffect(() => {
    (async () => {
      await initializeNewPageState();
    })();
  }, []);

  const handleCreate = async () => {
    if (brandSelect === null) return;
    const minTime = calcMinTime(MIN_SCHEDULED_DATE_PLUS_MINUTES);
    if (
      deliveryMethod === 'dateAndTime' &&
      isValidateBeforeDate(scheduledDate, minTime)
    ) {
      alert(
        `日時指定は最低${MIN_SCHEDULED_DATE_PLUS_MINUTES}分の間隔を空けて入力してください。`
      );
      return;
    }
    const scheduled_date = datetimeNoColonFormat(scheduledDate);
    try {
      setIsLoading(true);
      await useMenuManagementCreateExecution({
        associate_brand_id: Number(brandSelect?.value),
        scheduled_date:
          deliveryMethod === 'immediately' ? null : scheduled_date,
      });
      setIsLoading(false);
      alert('メニュー配信を登録しました。');
      history.push(listUrl);
    } catch (error) {
      setIsLoading(false);
      if (error instanceof HttpError && error.status === 409) {
        return alert(error.message);
      }
      // @ts-ignore TS2322
      errorHandler({ error });
    }
  };

  return (
    <PageLayout
      title={routes.menuManagementExecutionsNew.title}
      {...{ associateBrandSelectInput }}
    >
      <MenuManagementNewButtonArea
        listUrl="/menu-managements/executions"
        {...{
          handleCreate,
          isDisabledSubmitButton: false,
        }}
      />
      <CmScrollBox height={`calc(100vh - ${targetsHeight}px)`}>
        <MenuManagementCardWrapper>
          <CmStack spacing="20px">
            <CmRadioGroup
              value={deliveryMethod}
              onChange={handleChangeDeliveryMethod}
            >
              <CmFlex>
                <CmRadio size="md" value={deliveryMethodImmediately}>
                  {deliveryMethodMap.immediately}
                </CmRadio>
                <CmRadio ml="20px" size="md" value={deliveryMethodDateAndTime}>
                  {deliveryMethodMap.dateAndTime}
                </CmRadio>
              </CmFlex>
            </CmRadioGroup>

            {deliveryMethod === 'dateAndTime' && (
              <CmFormControl>
                <CmFormLabel isRequired fontWeight="bold">
                  日時設定
                </CmFormLabel>
                <CmFlex alignItems="center">
                  <CmButtonDatePicker
                    selectedDate={scheduledDate}
                    onChange={handleInputScheduledDate}
                    dateFormat={'yyyy - MM - dd'}
                    minDate={new Date()}
                  />
                  <CmBox ml="3" />
                  <CmSelectDigitsTime
                    date={scheduledDate}
                    handleChangeHour={(hour) =>
                      handleChangeTime(hour, 'setHours')
                    }
                    handleChangeMinutes={(minutes) =>
                      handleChangeTime(minutes, 'setMinutes')
                    }
                  />
                </CmFlex>
              </CmFormControl>
            )}
          </CmStack>
        </MenuManagementCardWrapper>
      </CmScrollBox>
      {isLoading && <CmLongLoading />}
    </PageLayout>
  );
};

export default MenuManagementExecutionsNewPage;
